const rootNodeSym = Symbol('rootNode');
const allNodesSym = Symbol('allNodes');
const parentOfSym = Symbol('parentOf');
const nodesSym = Symbol('nodes');
const addSym = Symbol('add');

/**
 * Simple add-only self-organising tree structure for objects that are not
 * already in a tree form. Provide a list of items for the tree and a function
 * to identify the parent of each item.
 *
 * @example
 * const items = [1,2,3,4,5,6,7,8,9,10]
 * const tree = new Tree(items, n => n>1?Math.floor((n+1)/2):null);
 * tree.rootNode.toArray() => [[1,[[2,[[3,[[5,[[9],[10]]],[6]]],[4,[[7],[8]]]]]]]]
 * t.rootNode.map(n => n * 2).toArray() => [[2,[[4,[[6,[[10,[[18],[20]]],[12]]],[8,[[14],[16]]]]]]]]
 * t.rootNode.fold((node, parent) => parent ? parent.item * node.item : node.item)
 *
 * @template T
 */
export class Tree {
  /**
   * @param {T[]} items
   * @param {((item:T) => T | null)} parentOf
   */
  constructor(items, parentOf) {
    /** @type {((item:T) => T | null)} parentOf */
    this[parentOfSym] = parentOf;
    /** * @type {Map<T, Node>} */
    this[allNodesSym] = new Map();
    /** @type {RootNode<T>} */
    this[rootNodeSym] = new RootNode();
    /** @type {T[]} */
    this.items = [];
    items.forEach(i => this.add(i));
  }

  /**
   * Get the root node. The root node will have no related item, it will only
   * contain zero or more child nodes. All descendants of the root node will
   * have related items.
   */
  get rootNode() {
    return this[rootNodeSym];
  }

  /**
   * Get the parent node of a given item
   *
   * @param {T} item
   * @returns {Node}
   */
  parentNode(item) {
    const parent = this[parentOfSym](item);
    if (!parent) return this[rootNodeSym];
    this.add(parent);
    return this[allNodesSym].get(parent);
  }

  /**
   * Add an item to the tree
   *
   * @param {T} item
   */
  add(item) {
    if (this[allNodesSym].has(item)) return;
    const parentNode = this.parentNode(item);
    const node = parentNode[addSym](item);
    this.items.push(item);
    this[allNodesSym].set(item, node);
  }
}

/**
 * @template T
 */
class Node {
  constructor() {
    /** @type {ItemNode<T>[]} */
    this[nodesSym] = [];
  }

  get nodes() {
    return this[nodesSym];
  }

  get children() {
    return this.nodes.map(n => n.item);
  }

  /**
   * @returns {T[]}
   */
  get descendants() {
    return this.nodes.reduce(
      (acc, node) => [...acc, node.item, ...node.descendants],
      []
    );
  }

  /**
   * @param {T|ItemNode<T>} item
   */
  [addSym](item) {
    const node = item instanceof ItemNode ? item : new ItemNode(item);
    this[nodesSym].push(node);
    return node;
  }

  /**
   * Apply a mapping function to every descendant
   *
   * @template T2
   * @param {((item: T) => T2)} fn
   */
  mapNodes(fn) {
    return this.nodes.map(n => n.map(fn));
  }

  /**
   * Traverse the entire tree. Calling `this.break()` will abort
   *
   * @param {((this: Signal, node: ItemNode<T>, parent: ItemNode<T>) => void)} fn
   * @param {{}=} options
   */
  traverse(fn, options) {
    // @ts-ignore
    const signal = options?.signal || new Signal();
    if (signal.current === 'break') return;

    for (let node of this.nodes) {
      fn.apply(signal, [node, this]);
      if (signal.current === 'break') return;
      node.traverse(fn, { signal });
      if (signal.current === 'break') return;
    }
  }

  /**
   * @returns {import("lodash").RecursiveArray<T>}
   */
  toArray() {
    return this.nodes.map(n => n.toArray());
  }
}

/**
 * @template T
 * @extends {Node<T>}
 */
class RootNode extends Node {
  /**
   * Apply a mapping function to this node and every descendant, creating a new
   * subtree
   *
   * @template T2
   * @param {((item: T) => T2)} fn
   * @returns {Node<T2>}
   */
  map(fn) {
    const node = new Node();
    node[nodesSym] = this.mapNodes(fn);
    return node;
  }

  /**
   * Apply a function recursively to this node and all descendants, producing a
   * single value for each top level node. Each child is passed to the function
   * first, and then the parent is passed with the mapped child data as the
   * second argument. This allows processing the tree into a different nested or
   * reduced structure.
   *
   * For example, we might want to simplify it down to { name: item.name,
   * children: [ { name: child name }, { name: child name }]} }
   *
   * @example
   *   tree.rootNode.fold((item, children) => children.length > 0 ? {name: item.name, children} : {name: item.name});
   *
   * @template T2
   * @param {((item: T, children: T2[]) => T2)} fn
   */
  fold(fn) {
    return this.nodes.map(n => n.fold(fn));
  }
}

/**
 * @template T
 * @extends Node<T>
 */
class ItemNode extends Node {
  /**
   * @param {T} item
   */
  constructor(item) {
    super();
    this.item = item;
  }

  /**
   * @see RootNode#map
   * @template T2
   * @param {((item: T) => T2)} fn
   * @returns {ItemNode<T2>}
   */
  map(fn) {
    const node = new ItemNode(fn(this.item));
    node[nodesSym] = this.mapNodes(fn);
    return node;
  }

  /**
   * @see RootNode#fold
   * @template T2
   * @param {((item: T, children: T2[]) => T2)} fn
   * @returns {T2}
   */
  fold(fn) {
    const children = this.nodes.map(n => n.fold(fn));
    return fn(this.item, children);
  }

  /**
   * @returns {import("lodash").RecursiveArray<T>}
   */
  toArray() {
    if (this.nodes.length > 0) {
      return [this.item, this.nodes.map(n => n.toArray())];
    } else {
      return [this.item];
    }
  }
}

class Signal {
  constructor() {
    this.current = 'continue';
  }
  break() {
    this.current = 'break';
  }
}
